import {
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconCalendarEvent, IconTruckDelivery } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { usePravica } from '../seja/pravice';
import { barve } from '../tema';
import { STATUSI, type StatusNaloga } from './delavnica';
import { ura, type Termin } from './termini';

/**
 * Delavnica na začetnem zaslonu: odprti nalogi po statusih (klik odpre seznam s tem statusom), koliko vozil je bilo
 * danes sprejetih in današnji termini (»Sprejem« odpre sprejem vozila s podatki termina).
 */
export function PregledDelavnice() {
  const pregled = useQuery({
    queryKey: ['delavnica-pregled'],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/pregled', { signal })),
  });
  return (
    <Paper radius="lg" p="md" shadow="xs">
      <Group justify="space-between" mb="sm">
        <Title order={2} size="h4">
          Delavnica
        </Title>
        <Button
          component={Link}
          to="/delavnica/termini"
          variant="subtle"
          size="compact-md"
          leftSection={<IconCalendarEvent size={18} />}
        >
          Termini
        </Button>
      </Group>
      {pregled.isError ? (
        <Text size="sm" c="red">
          Pregleda delavnice ni bilo mogoče naložiti.
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
          {pregled.data ? (
            <>
              {(pregled.data.statusi ?? []).map((s) => (
                <Ploscica
                  key={s.status}
                  stevilo={s.stevilo ?? 0}
                  oznaka={STATUSI[s.status as StatusNaloga].naziv}
                  na={`/delavnica/nalogi?status=${s.status}`}
                />
              ))}
              <Ploscica
                stevilo={pregled.data.danesSprejeti ?? 0}
                oznaka="Danes sprejeti"
                na="/delavnica/nalogi?status=VSI"
              />
            </>
          ) : (
            Array.from({ length: 6 }, (_, i) => <Skeleton key={i} h={72} radius="md" />)
          )}
        </SimpleGrid>
      )}
      {(pregled.data?.termini ?? []).length > 0 && <DanasnjiTermini termini={pregled.data!.termini!} />}
    </Paper>
  );
}

function Ploscica({ stevilo, oznaka, na }: { stevilo: number; oznaka: string; na: string }) {
  return (
    <UnstyledButton
      component={Link}
      to={na}
      p="sm"
      style={{ borderRadius: 'var(--mantine-radius-md)', border: `1px solid ${barve.obroba}` }}
      aria-label={`${oznaka}: ${stevilo}`}
    >
      <Text fw={700} fz={28} lh={1.1} c={stevilo > 0 ? barve.primarna : 'dimmed'}>
        {stevilo}
      </Text>
      <Text size="sm" c="dimmed">
        {oznaka}
      </Text>
    </UnstyledButton>
  );
}

/** Današnji termini po času: vozilo, stranka, kaj je treba narediti; nesprejet ima gumb »Sprejem«, sprejet nalog. */
function DanasnjiTermini({ termini }: { termini: Termin[] }) {
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  return (
    <>
      <Divider my="md" />
      <Text fw={700} mb="xs">
        Danes naročeni
      </Text>
      <Stack gap={6}>
        {termini.map((t) => (
          <Group key={t.id} justify="space-between" wrap="nowrap" gap="sm" data-termin={t.id}>
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <Text fw={700} c={barve.primarna} w={48} style={{ flexShrink: 0 }}>
                {ura(t.zacetek)}
              </Text>
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} truncate>
                  {[t.vozilo?.registrska, [t.vozilo?.znamka, t.vozilo?.model].filter(Boolean).join(' '), t.kupec]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                {t.opis && (
                  <Text size="xs" c="dimmed" truncate>
                    {t.opis}
                  </Text>
                )}
              </div>
            </Group>
            {t.nalogId ? (
              <Anchor component={Link} to={`/delavnica/nalogi/${t.nalogId}`} style={{ flexShrink: 0 }}>
                <Badge color="green" variant="light">
                  Nalog {t.stevilkaNaloga}
                </Badge>
              </Anchor>
            ) : (
              ureja && (
                <Button
                  component={Link}
                  to={`/delavnica/sprejem?termin=${t.id}`}
                  variant="light"
                  size="compact-md"
                  leftSection={<IconTruckDelivery size={16} />}
                  style={{ flexShrink: 0 }}
                >
                  Sprejem
                </Button>
              )
            )}
          </Group>
        ))}
      </Stack>
    </>
  );
}
